import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/?.test(formData?.phone?.replace(/\s+/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit Indian mobile number';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData?.message?.trim()?.length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        message: ''
      });
    }, 2000);
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card rounded-2xl p-8 shadow-elevation-2 text-center"
      >
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircle" size={32} color="var(--color-success)" />
        </div>
        <h3 className="text-2xl font-semibold text-foreground mb-4">
          Message Sent Successfully!
        </h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Thank you for contacting Maayo Fabric Care. We have received your inquiry and will respond within 2-4 business hours during our operating hours.
        </p>
        <Button
          variant="outline"
          onClick={() => setIsSubmitted(false)}
          iconName="RotateCcw"
          iconPosition="left"
        >
          Send Another Message
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-card rounded-2xl p-8 shadow-elevation-2"
    >
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-foreground mb-4">
          Get in Touch
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          Have questions about our services or need to schedule a pickup? We're here to help! Fill out the form below and we'll get back to you promptly.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Full Name"
            name="name"
            type="text"
            placeholder="Enter your full name"
            value={formData?.name}
            onChange={handleInputChange}
            error={errors?.name}
            required
            className="transition-smooth"
          />

          <Input
            label="Phone Number"
            name="phone"
            type="tel"
            placeholder="Enter your mobile number"
            value={formData?.phone}
            onChange={handleInputChange}
            error={errors?.phone}
            required
            className="transition-smooth"
          />
        </div>

        <Input
          label="Email Address"
          name="email"
          type="email"
          placeholder="Enter your email address"
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          required
          className="transition-smooth"
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Message <span className="text-error">*</span>
          </label>
          <textarea
            name="message"
            rows={5}
            placeholder="Tell us about your laundry needs, pickup preferences, or any questions you have..."
            value={formData?.message}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-lg bg-input text-foreground placeholder:text-muted-foreground transition-smooth focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none ${
              errors?.message ? 'border-error' : 'border-border'
            }`}
          />
          {errors?.message && (
            <p className="mt-2 text-sm text-error">{errors?.message}</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <Button
            type="submit"
            variant="default"
            loading={isSubmitting}
            iconName="Send"
            iconPosition="right"
            className="flex-1 transition-smooth hover:hover-scale"
          >
            {isSubmitting ? 'Sending Message...' : 'Send Message'}
          </Button>
          
          {/* <Button
            type="button"
            variant="outline"
            iconName="Phone"
            iconPosition="left"
            onClick={() => window.open('tel:+919600044004')}
            className="transition-smooth hover:hover-scale"
          >
            Call Now
          </Button> */}
        </div>
      </form>
      <div className="mt-8 pt-6 border-t border-border">
        <p className="text-sm text-muted-foreground text-center mb-6">
          <Icon name="Clock" size={16} className="inline mr-2" />
          We typically respond within 2-4 hours during business hours
        </p>
        
        {/* Service Coverage Area */}
        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Service Coverage Area
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-foreground mb-3">Primary Areas</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Saravanampatti</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Chinnavedampatti</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Kalapatti</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Villankurichi</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Keeranatham</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Karattumedu</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Ganapathi</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Nehru Nagar</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Cheran Ma Nagar</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Thudiyalur</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>NGGO Colony</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>GN Mills</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Kavundampalayam</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Edayarpalayam</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-success)" />
                  <span>Kanuvai</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-foreground mb-3">Extended Coverage</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-warning)" />
                  <span>Saibaba Colony</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-warning)" />
                  <span>Vadavalli</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-warning)" />
                  <span>RS Puram</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-warning)" />
                  <span>Gandhipuram Central</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-warning)" />
                  <span>Peelamedu & Nearby Areas</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-warning)" />
                  <span>Brookefields Mall Area</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-warning)" />
                  <span>Avinashi Road & Surroundings</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <Icon name="Info" size={16} className="inline mr-2" />
              Don't see your area listed? Contact us to check if we can arrange special pickup services for your location.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ContactForm;